import React, { CSSProperties, ReactNode, useState } from 'react';
import { useParams } from 'react-router-dom';
import DataTableNew from '../../../components/main/DataTableNew/DataTableNew';
import { UserRoleEnum, useGetClientQuery } from '../../../generated/graphql';
import ShowOnRole from '../../../components/main/ShowOnRole/ShowOnRole';
import NewClientAdminDialog from '../../../components/dialogs/NewClientAdminDialog/NewClientAdminDialog';
import DeleteClientAdminDialog from '../../../components/dialogs/DeleteClientAdminDialog/DeleteClientAdminDialog';
import SpinnerNew from '../../../components/main/SpinnerNew/SpinnerNew';
import { Plus, Trash2, Shield, LayoutGrid, List } from 'lucide-react';
import RefreshButton from '../../../components/main/RefreshButton/RefreshButton';

type AccessControlProps = {
    style?: CSSProperties | undefined,
    children?: ReactNode | undefined,
};

const getInitials = (name?: string, email?: string) => {
    if (name) {
        const parts = name.trim().split(/\s+/);
        return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase();
    }
    return email ? email.substring(0, 2).toUpperCase() : '??';
};

const DeleteAdminCell = ({ row, clientId, onRefetch }: { row: any; clientId: string; onRefetch: () => void }) => {
    const [deleteAdminVisible, setDeleteAdminVisible] = useState(false);
    return (
        <>
            <button className="btn-icon p-1 rounded" style={{ color: 'var(--danger)' }} onClick={() => setDeleteAdminVisible(true)}>
                <Trash2 size={14} />
            </button>
            {deleteAdminVisible && (
                <DeleteClientAdminDialog visible={deleteAdminVisible} clientId={clientId} ids={[row?.id]} onClose={() => { setDeleteAdminVisible(false); onRefetch(); }} />
            )}
        </>
    );
};

const AccessControl = (props: AccessControlProps) => {
    const { clientId } = useParams();
    const [newClientAdminVisible, setNewClientAdminVisible] = useState(false);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [selectedAdminIds, setSelectedAdminIds] = useState<string[]>([]);
    const [deleteAdminId, setDeleteAdminId] = useState<string | null>(null);
    const [deleteSelectedVisible, setDeleteSelectedVisible] = useState(false);

    const client = useGetClientQuery({
        variables: {
            id: clientId
        }
    });

    const handleNewClientAdminClose = () => {
        client.refetch();
        setNewClientAdminVisible(false);
    };

    const isAdmin = client.data?.Client?.isAdmin || false;
    const admins = (client.data?.Client?.admins as any[]) || [];

    return (
        <div className="flex flex-col h-full">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b" style={{ borderColor: 'var(--border-default)' }}>
                <RefreshButton onRefresh={() => client.refetch()} />
                <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                    <button className="btn btn-ghost text-sm" onClick={() => setNewClientAdminVisible(true)}>
                        <Plus size={15} /> New Admin
                    </button>
                    {selectedAdminIds.length > 0 && viewMode === 'grid' && (
                        <>
                            <div className="w-px h-5 mx-1" style={{ backgroundColor: 'var(--border-default)' }} />
                            <button className="btn btn-ghost text-sm" style={{ color: 'var(--danger)' }} onClick={() => setDeleteSelectedVisible(true)}>
                                <Trash2 size={15} /> Remove ({selectedAdminIds.length})
                            </button>
                        </>
                    )}
                </ShowOnRole>
                <div className="flex-1" />
                <div className="flex gap-1 p-1 rounded-lg" style={{ backgroundColor: 'var(--surface-100)' }}>
                    <button
                        className="p-2 rounded-md transition-all"
                        style={{
                            backgroundColor: viewMode === 'grid' ? 'var(--bg-card)' : 'transparent',
                            boxShadow: viewMode === 'grid' ? 'var(--shadow-sm)' : 'none',
                            color: viewMode === 'grid' ? 'var(--text-primary)' : 'var(--text-tertiary)',
                            border: 'none', cursor: 'pointer', outline: 'none',
                        }}
                        onClick={() => setViewMode('grid')}
                    >
                        <LayoutGrid size={16} />
                    </button>
                    <button
                        className="p-2 rounded-md transition-all"
                        style={{
                            backgroundColor: viewMode === 'list' ? 'var(--bg-card)' : 'transparent',
                            boxShadow: viewMode === 'list' ? 'var(--shadow-sm)' : 'none',
                            color: viewMode === 'list' ? 'var(--text-primary)' : 'var(--text-tertiary)',
                            border: 'none', cursor: 'pointer', outline: 'none',
                        }}
                        onClick={() => setViewMode('list')}
                    >
                        <List size={16} />
                    </button>
                </div>
            </div>

            <div className="flex flex-col flex-1 overflow-hidden">
                {viewMode === 'list' ? (
                    <DataTableNew
                        sortingMode="client"
                        isLoading={client.loading}
                        data={admins}
                        columns={[
                            { id: 'email', name: 'Email', sortable: true },
                            {
                                id: '_actions', name: '', width: '55px',
                                render: (row) => (
                                    <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                                        <DeleteAdminCell row={row} clientId={clientId || ""} onRefetch={() => client.refetch()} />
                                    </ShowOnRole>
                                )
                            }
                        ]}
                        showName={false}
                        searchable={false}
                    />
                ) : (
                    <div className="flex-1 overflow-auto p-4">
                        {client.loading ? (
                            <SpinnerNew />
                        ) : admins.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16" style={{ color: 'var(--text-tertiary)' }}>
                                <Shield size={32} className="mb-3 opacity-50" />
                                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>No admins</p>
                                <p className="text-xs mt-1">Add an admin to manage this client</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 animate-fade-in">
                                {admins.map((admin: any) => {
                                    const isSelected = selectedAdminIds.includes(admin.id);
                                    return (
                                        <div
                                            key={admin.id}
                                            className="card p-5 group relative transition-all"
                                            style={{
                                                borderColor: isSelected ? 'var(--accent)' : undefined,
                                                backgroundColor: isSelected ? 'var(--accent-soft)' : undefined,
                                            }}
                                        >
                                            <ShowOnRole roles={[UserRoleEnum.Admin]} isAdmin={isAdmin}>
                                                <div className="absolute top-4 right-4 flex items-center gap-2">
                                                    <button
                                                        className="btn btn-ghost p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        style={{ color: 'var(--danger)' }}
                                                        onClick={() => setDeleteAdminId(admin.id)}
                                                        title="Remove admin"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        onChange={() => {
                                                            setSelectedAdminIds(prev =>
                                                                prev.includes(admin.id) ? prev.filter((i: string) => i !== admin.id) : [...prev, admin.id]
                                                            );
                                                        }}
                                                        className="w-4 h-4 rounded cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
                                                        style={{
                                                            accentColor: 'var(--accent)',
                                                            opacity: isSelected ? 1 : undefined,
                                                        }}
                                                    />
                                                </div>
                                            </ShowOnRole>

                                            <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3 text-sm font-semibold"
                                                style={{ backgroundColor: 'var(--accent-soft)', color: 'var(--accent)' }}>
                                                {getInitials(admin.name, admin.email)}
                                            </div>

                                            {admin.name && (
                                                <p className="text-sm font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>
                                                    {admin.name}
                                                </p>
                                            )}
                                            <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                                                {admin.email}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <NewClientAdminDialog visible={newClientAdminVisible} onClose={handleNewClientAdminClose} clientId={clientId ?? ""} />
            {deleteAdminId && <DeleteClientAdminDialog visible={!!deleteAdminId} clientId={clientId || ""} ids={[deleteAdminId]} onClose={() => { setDeleteAdminId(null); client.refetch(); }} />}
            {deleteSelectedVisible && <DeleteClientAdminDialog visible={deleteSelectedVisible} clientId={clientId || ""} ids={selectedAdminIds} onClose={() => { setDeleteSelectedVisible(false); setSelectedAdminIds([]); client.refetch(); }} />}
        </div>
    );
};

export default AccessControl;
